import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchClasses, deleteClass } from '../../api/classes';
import { getSubjectColor } from '../../styles/colors';
import styles from './ClassesPage.module.css';

const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const navigate = useNavigate();

  const loadClasses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchClasses();
      setClasses(res.data || []);
    } catch (err) {
      setError('Failed to load classes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  const getSchoolYears = () => {
    const years = new Set();
    classes.forEach(cls => {
      if (cls.yearStart && cls.yearEnd) {
        const startYear = new Date(cls.yearStart).getFullYear();
        const endYear = new Date(cls.yearEnd).getFullYear();
        years.add(`${startYear}-${endYear}`);
      }
    });
    return Array.from(years).sort().reverse();
  };

  const schoolYears = getSchoolYears();

  const filteredClasses = selectedYear === 'all'
    ? classes
    : classes.filter(cls => {
        const startYear = new Date(cls.yearStart).getFullYear();
        const endYear = new Date(cls.yearEnd).getFullYear();
        return `${startYear}-${endYear}` === selectedYear;
      });

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? This will also remove all associated lessons.`)) return;
    try {
      setDeletingId(id);
      await deleteClass(id);
      setClasses(prev => prev.filter(c => c.id !== id));
    } catch {
      setError('Failed to delete class.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDateRange = (start, end) => {
    const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>My Classes</h1>
          <p className={styles.subtitle}>{filteredClasses.length} {filteredClasses.length === 1 ? 'class' : 'classes'}</p>
          {schoolYears.length > 1 && (
            <select
              className={styles.yearSelect}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">All Years</option>
              {schoolYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>
        <Link to="/onboarding" className={styles.createBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create New Class
        </Link>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {classes.length === 0 ? (
        <div className={styles.empty}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="1.5">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          <h2>No classes yet</h2>
          <p>Create your first class to start planning lessons.</p>
          <Link to="/onboarding" className={styles.createBtn}>Create Your First Class</Link>
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className={styles.empty}>
          <p>No classes for this school year.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {filteredClasses.map(cls => {
            const color = getSubjectColor(cls.subject);
            return (
              <div
                key={cls.id}
                className={styles.card}
                onClick={() => navigate(`/classes/${cls.id}`)}
                style={{ borderTopColor: color.border }}
              >
                <div className={styles.cardHeader}>
                  <h2 className={styles.cardTitle}>{cls.name}</h2>
                  <button
                    className={styles.deleteBtn}
                    onClick={(e) => handleDelete(e, cls.id, cls.name)}
                    disabled={deletingId === cls.id}
                    title="Delete class"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
                <div className={styles.badges}>
                  <span className={styles.badge} style={{ backgroundColor: color.bg, color: color.text }}>{cls.subject}</span>
                  <span className={styles.badge}>{cls.gradeLevel}</span>
                  {cls.state && <span className={styles.badge}>{cls.state}</span>}
                </div>
                <p className={styles.dateRange}>{formatDateRange(cls.yearStart, cls.yearEnd)}</p>
                <div className={styles.stats}>
                  <span>{cls._count?.lessons ?? 0} lessons</span>
                  <span>{cls._count?.worksheets ?? 0} activities</span>
                  <span>{cls.units?.length ?? 0} units</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassesPage;
